import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import Breadcrumb from "./Breadcrumb";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import axios, { AxiosError } from "axios";
import Confetti from "react-confetti";

import { useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { clarity } from "react-microsoft-clarity";

function FreeTrialBanner({
  status,
  openDialog,
}: {
  status: string | undefined;
  openDialog: () => void;
}) {
  if (status === "trialing") {
    return (
      <>
        <span className="font-medium text-sm">
          You're on the free trial. Want to generate 60-90 second videos?
        </span>
        <button
          onClick={openDialog}
          className="bg-white text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-md text-sm font-medium transition-colors hover:cursor-pointer"
        >
          Activate Now
        </button>
      </>
    );
  }

  if (status === "waiting") {
    return (
      <span className="font-medium text-sm">
        Start a Free Trial to get 10 Free Videos
      </span>
    );
  }

  return <></>;
}

export default function AppLayout() {
  const location = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const { user } = useAuth();

  const confettiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clarity.identify(user?.email || "Unknown", {
      userProperty: "value",
    });
  }, [user]);

  useEffect(() => {
    // Update window size for confetti
    if (isResponseDialogOpen && apiResponse?.success) {
      const updateWindowSize = () => {
        if (confettiRef.current) {
          setWindowSize({
            width: confettiRef.current.clientWidth,
            height: confettiRef.current.clientHeight,
          });
        }
      };

      updateWindowSize();
      window.addEventListener("resize", updateWindowSize);

      return () => window.removeEventListener("resize", updateWindowSize);
    }
  }, [isResponseDialogOpen, apiResponse]);

  // Get the current page title based on the route
  const getPageTitle = () => {
    const path = location.pathname;

    if (path.includes("/product-videos")) return "Product Videos";
    if (path.includes("/talking-videos")) return "Talking Videos";
    if (path.includes("/filter")) return "Filter";
    if (path.includes("/faq")) return "FAQ";
    if (path.includes("/affiliate")) return "Affiliate";
    if (path.includes("/discord")) return "Discord";
    if (path.includes("/generate-video")) return "Generate Video";

    return "Dashboard";
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const closeResponseDialog = () => {
    setIsResponseDialogOpen(false);
  };

  const navigateToVideoGeneration = () => {
    closeResponseDialog();
    // Assuming this is how navigation works in your app
    window.location.href = "/dashboard";
  };

  const handleAuthorize = async () => {
    try {
      setIsLoading(true);
      const response = await api.post("/api/payment/start-now");
      setApiResponse(response.data);
      closeDialog();
      setIsResponseDialogOpen(true);
    } catch (error) {
      const axiosError = error as AxiosError;
      setApiResponse(
        axiosError.response?.data || { error: "Request failed", success: false }
      );
      closeDialog();
      setIsResponseDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-100 dark:bg-gray-950 transition-colors duration-200">
      {user?.is_premium !== "active" && (
        <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 text-white py-2.5 px-6 shadow-md z-50 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <FreeTrialBanner
              status={user?.is_premium}
              openDialog={openDialog}
            />
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Breadcrumb title={getPageTitle()} />
          <main className="flex-1 overflow-auto p-6">
            <div className="rounded-xl h-full overflow-auto transition-colors duration-200">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Headless UI Dialog */}
      <Transition appear show={isDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-800/75 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Upgrade to Pro Plan
                  </Dialog.Title>
                  <div className="mt-4">
                    <p className="text-md text-gray-500 dark:text-gray-300">
                      Your free trial will end and you will be charged for the
                      plan you picked at signup immediately.
                    </p>
                  </div>

                  <div className="mt-4 rounded-lg ">
                    <ul className="space-y-3 text-sm">
                      {/* <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">
                          <span className="font-bold">20</span> videos per month
                          (up to 1 minute 30 seconds)
                        </span>
                      </li> */}
                      <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">Hold Any Product</span>
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">100+ AI actors</span>
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">100+ AI voices</span>
                      </li>
                      {/* <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">
                          AI script writer (trained on 1,000 winning ads)
                        </span>
                      </li> */}
                      <li className="flex items-center">
                        <span className="mr-2 text-green-600 dark:text-green-400 text-lg">
                          ✓
                        </span>
                        <span className="font-medium">
                          Generated within minutes
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-200 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 hover:cursor-pointer"
                      onClick={closeDialog}
                    >
                      Maybe Later
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 hover:cursor-pointer"
                      onClick={handleAuthorize}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        "Authorize"
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Success/Error Response Dialog */}
      <Transition appear show={isResponseDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={closeResponseDialog}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-800/75 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                {apiResponse?.success ? (
                  <Dialog.Panel
                    ref={confettiRef}
                    className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all relative"
                  >
                    {/* Confetti effect */}
                    <Confetti
                      width={windowSize.width}
                      height={windowSize.height}
                      recycle={false}
                      numberOfPieces={200}
                    />

                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold leading-6 text-green-600 dark:text-green-400 mb-2"
                    >
                      Success! 🎉
                    </Dialog.Title>

                    <div className="mt-2">
                      <p className="text-md text-gray-700 dark:text-gray-300">
                        {apiResponse?.message ||
                          "Your account has been upgraded to Pro!"}
                      </p>

                      <div className="mt-4">
                        <p className="text-md text-gray-700 dark:text-gray-300">
                          You now have access to longer video generation!
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-center">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-6 py-3 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 hover:cursor-pointer transition-all duration-200 shadow-md hover:shadow-lg"
                        onClick={navigateToVideoGeneration}
                      >
                        Start Generating UGC
                      </button>
                    </div>
                  </Dialog.Panel>
                ) : (
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold leading-6 text-red-600 dark:text-red-400 mb-2"
                    >
                      Something went wrong
                    </Dialog.Title>

                    <div className="mt-2">
                      <p className="text-md text-gray-700 dark:text-gray-300">
                        There was an unknown error.
                      </p>

                      <div className="mt-4 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Please contact our support team at{" "}
                          <a
                            href="mailto:support@ugzai.com"
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            support@ugz.ai
                          </a>{" "}
                          for assistance with your payment.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-200 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 hover:cursor-pointer"
                        onClick={closeResponseDialog}
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                )}
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
